import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
export function PatientExperience() {
  const steps = [{
    number: '01',
    title: 'Initial Consultation',
    description: 'Connect with a specialized healthcare provider who will evaluate your needs and create a personalized treatment plan.'
  }, {
    number: '02',
    title: 'Personalized Treatment',
    description: 'Receive a custom treatment plan including either custom compounded or name brand medications delivered directly to your door.'
  }, {
    number: '03',
    title: 'Ongoing Support',
    description: 'Get continuous care with unlimited messaging, regular check-ins, and real-time adjustments to your treatment.'
  }, {
    number: '04',
    title: 'Measurable Results',
    description: 'Track your medications along with your health metrics such as weight, activity, and nutrition to find the correlations that help you live your healthiest life.'
  }];
  return <section className="py-24 relative overflow-hidden" data-id="element-236">
      {/* Decorative elements */}
      <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-[#BBDDFF]/10 blur-3xl" data-id="element-237"></div>
      <div className="absolute -bottom-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-[#C85A15]/5 blur-3xl" data-id="element-238"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-239">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-240">
          <div data-id="element-241">
            <div className="inline-block mb-4 px-4 py-1 rounded-full bg-gradient-to-r from-[#BBDDFF]/10 to-[#C85A15]/10 backdrop-blur-sm border border-white/10" data-id="element-242">
              <span className="text-sm font-medium" data-id="element-243">Patient Experience</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-244">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#C85A15] bg-clip-text text-transparent" data-id="element-245">
                Better than in-person care
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-246">
              Our platform delivers an exceptional value based care experience
              with HIPAA compliant video visits, personalized attention, and
              continuous support that traditional in-office visits simply can't
              match.
            </p>
            <div className="space-y-4 mb-8" data-id="element-247">
              <div className="flex items-start" data-id="element-248">
                <CheckCircle className="text-[#C85A15] mr-3 mt-1 flex-shrink-0" size={20} data-id="element-249" />
                <p className="text-gray-700 dark:text-gray-200" data-id="element-250">
                  24/7 access to your care team
                </p>
              </div>
              <div className="flex items-start" data-id="element-251">
                <CheckCircle className="text-[#C85A15] mr-3 mt-1 flex-shrink-0" size={20} data-id="element-252" />
                <p className="text-gray-700 dark:text-gray-200" data-id="element-253">
                  No waiting rooms or scheduling delays
                </p>
              </div>
              <div className="flex items-start" data-id="element-254">
                <CheckCircle className="text-[#C85A15] mr-3 mt-1 flex-shrink-0" size={20} data-id="element-255" />
                <p className="text-gray-700 dark:text-gray-200" data-id="element-256">
                  Real-time health data sharing with your provider
                </p>
              </div>
              <div className="flex items-start" data-id="element-257">
                <CheckCircle className="text-[#C85A15] mr-3 mt-1 flex-shrink-0" size={20} data-id="element-258" />
                <p className="text-gray-700 dark:text-gray-200" data-id="element-259">
                  Medications delivered directly to your door
                </p>
              </div>
            </div>
            <button className="hidden flex items-center text-gray-900 dark:text-white font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-3 px-6 rounded-full hover:shadow-lg transition-all group" data-id="element-260">
              See patient success stories
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} data-id="element-261" />
            </button>
          </div>
          <div className="relative" data-id="element-262">
            <div className="absolute inset-0 bg-gradient-to-br from-[#BBDDFF]/20 to-[#C85A15]/20 rounded-3xl blur-xl" data-id="element-263"></div>
            <div className="relative bg-white dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 dark:border-gray-700/30" data-id="element-264">
              <div className="space-y-8" data-id="element-265">
                {steps.map((step, index) => <div key={index} className="flex" data-id="element-266">
                    <div className="mr-6" data-id="element-267">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#BBDDFF] to-[#C85A15] flex items-center justify-center text-white font-bold" data-id="element-268">
                        {step.number}
                      </div>
                      {index < steps.length - 1 && <div className="h-full w-px bg-gradient-to-b from-[#BBDDFF] to-[#C85A15] mx-auto my-2" data-id="element-269"></div>}
                    </div>
                    <div className={index < steps.length - 1 ? 'pb-8' : ''} data-id="element-270">
                      <h3 className="text-xl font-semibold mb-2" data-id="element-271">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300" data-id="element-272">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </div>
              <div className="hidden mt-8 p-4 bg-gradient-to-r from-[#BBDDFF]/10 to-[#C85A15]/10 rounded-xl border border-white/10" data-id="element-273">
                <div className="flex items-center" data-id="element-274">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80" alt="Patient testimonial" className="w-12 h-12 rounded-full object-cover mr-4" data-id="element-275" />
                  <div data-id="element-276">
                    <p className="text-sm text-gray-600 dark:text-gray-300 italic" data-id="element-277">
                      "JoinOmu transformed my healthcare experience. The
                      continuous support and personalized care have helped me
                      achieve results I never thought possible."
                    </p>
                    <p className="text-sm font-medium mt-1" data-id="element-278">
                      Sarah K., Weight Loss Patient
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}